'use client'

import { useEffect, useState } from 'react'
import { CardContent, CardFooter, CardHeader, CardTitle, CardV2 } from '../../../components/CardV2/CardV2'
import SectionTitle from '../../../components/SectionTitle/SectionTitle'
import { useTranslation } from 'react-i18next'
import Banner from '../../../components/Banner/Banner'
import { ButtonV2 } from '../../../components/ButtonV2/ButtonV2'
import { toastService } from "../../../services/ToastService";
import { CheckCircle, ExternalLink, Loader2, Settings, FileText, MessageCircle } from 'lucide-react'
import PaymentsAPI, { StripeAccountSummaryResponse } from '../../../api/PaymentsAPI'
import CONFIG from '../../../config/config'
import FAQ from '../../../components/FAQ/FAQ'
import InvoicesTab from './InvoicesTab'
import PaymentMethodsTab from './PaymentMethodsTab'

export default function PaymentSegment() {
  const { t } = useTranslation();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [bannerConfig, setBannerConfig] = useState<{
    title: string;
    description: string;
    actionLabel: string;
    action: () => void;
  } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isOnboarding, setIsOnboarding] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>(t("settings.payments.loading.checking_status")!);
  const [activeTab, setActiveTab] = useState<'overview' | 'methods' | 'invoices'>('overview');
  const [accountSummary, setAccountSummary] = useState<StripeAccountSummaryResponse | null>(null);
  const [isAccountSummaryLoading, setIsAccountSummaryLoading] = useState<boolean>(false);
  const [accountSummaryError, setAccountSummaryError] = useState<boolean>(false);

  useEffect(() => {
    checkConnectionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkConnectionStatus = async () => {
    setIsLoading(true);
    setLoadingMessage(t("settings.payments.loading.checking_status")!);
    try {
      const response = await PaymentsAPI.getConnectionStatus();
      const connected = response.data.connected;
      setIsConnected(connected);
      
      const config = getBannerConfigForReason(response.data.reason, response.data.details);
      setBannerConfig(config);

      if (connected) {
        void loadAccountSummary();
      } else {
        setAccountSummary(null);
        setAccountSummaryError(false);
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
      toastService.showToast(t("settings.payments.errors.connection_check"), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccountSummary = async () => {
    setIsAccountSummaryLoading(true);
    setAccountSummaryError(false);

    try {
      const response = await PaymentsAPI.getAccountSummary();
      setAccountSummary(response.data);
    } catch (error) {
      console.error('Error loading Stripe account summary:', error);
      setAccountSummary(null);
      setAccountSummaryError(true);
    } finally {
      setIsAccountSummaryLoading(false);
    }
  };

  const startOnboarding = async () => {
    setIsOnboarding(true);
    setLoadingMessage(t("settings.payments.loading.preparing_onboarding")!);
    try {
      const response = await PaymentsAPI.startOnboarding();
      
      // Redirect to Stripe's onboarding flow
      window.location.href = response.data.onboardingUrl;
    } catch (error) {
      console.error('Error starting onboarding process:', error);
      toastService.showToast(t("settings.payments.errors.onboarding_start"), 'error');
      setIsOnboarding(false);
    }
  };

  const navigateToStripeDashboard = async () => {
    if (!isConnected) return;
    
    setIsLoading(true);
    setLoadingMessage(t("settings.payments.loading.generating_link")!);
    
    try {
      const response = await PaymentsAPI.getDashboardLink();
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error generating dashboard link:', error);
      toastService.showToast(t("settings.payments.errors.dashboard_link"), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getBannerConfigForReason = (reason?: string, details?: string) => {
    switch (reason) {
      case 'REQUIRES_UPDATE':
        return {
          title: t("settings.payments.status.requires_update.title") || "Account Update Required",
          description: details || t("settings.payments.status.requires_update.description") || "Your Stripe account needs attention to continue processing payments.",
          actionLabel: t("settings.payments.status.requires_update.cta") || "Update Account",
          action: startOnboarding
        };
      case 'REJECTED':
        return {
          title: t("settings.payments.status.rejected.title") || "Account Rejected",
          description: details || t("settings.payments.status.rejected.description") || "Your Stripe account was rejected. Please contact support for assistance.",
          actionLabel: t("settings.payments.status.rejected.cta") || "Contact Support",
          action: () => window.open('mailto:support@example.com', '_blank')
        };
      case 'RESTRICTED':
        return {
          title: t("settings.payments.status.restricted.title") || "Account Under Review",
          description: details || t("settings.payments.status.restricted.description") || "Your account is currently under review. This may take a few business days.",
          actionLabel: t("settings.payments.status.restricted.cta") || "Check Status",
          action: checkConnectionStatus
        };
      case 'DISCONNECTED':
        return {
          title: t("settings.payments.status.disconnected.title") || "Account Disconnected",
          description: details || t("settings.payments.status.disconnected.description") || "Your Stripe account has been disconnected. Please reconnect to continue.",
          actionLabel: t("settings.payments.status.disconnected.cta") || "Reconnect",
          action: startOnboarding
        };
      case 'INCOMPLETE_ONBOARDING':
        return {
          title: t("settings.payments.status.incomplete.title") || "Complete Onboarding",
          description: details || t("settings.payments.status.incomplete.description") || "Please complete your Stripe onboarding to start accepting payments.",
          actionLabel: t("settings.payments.status.incomplete.cta") || "Continue Setup",
          action: startOnboarding
        };
      case 'NOT_STARTED':
      case null:
      case undefined:
      default:
        return {
          title: t("settings.payments.banner.title") || "Connect Stripe Account",
          description: t("settings.payments.banner.description") || "Connect your Stripe account to start accepting payments from customers.",
          actionLabel: t("settings.payments.banner.cta") || "Get Started",
          action: startOnboarding
        };
    }
  };

  const formatAccountSummaryAmount = (cents: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    }
  };

  const renderAccountSummaryValue = (label: string, cents: number) => (
    <div className="rounded-md bg-muted/40 p-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 text-base font-semibold text-foreground">
        {accountSummary ? formatAccountSummaryAmount(cents, accountSummary.currency) : '-'}
      </p>
    </div>
  );

  return (
    <div className="container mx-auto space-y-6">
      <CardV2>
        <CardHeader>
          <CardTitle>{t("settings.payments.title")}</CardTitle>
          {isConnected && (
            <div className="flex space-x-1 mt-4">
              <ButtonV2
                variant={activeTab === 'overview' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('overview')}
                className="flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>{t("settings.payments.tabs.overview")}</span>
              </ButtonV2>
              {/* <ButtonV2
                variant={activeTab === 'methods' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('methods')}
                className="flex items-center space-x-2"
              >
                <CreditCard className="h-4 w-4" />
                <span>{t("settings.payments.tabs.methods")}</span>
              </ButtonV2> */}
              <ButtonV2
                variant={activeTab === 'invoices' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('invoices')}
                className="flex items-center space-x-2"
              >
                <FileText className="h-4 w-4" />
                <span>{t("settings.payments.tabs.invoices")}</span>
              </ButtonV2>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10 space-y-4">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium text-muted-foreground">{loadingMessage}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("settings.payments.loading.please_wait")}</p>
              </div>
            </div>
          ) : isConnected ? (
            <>
              {activeTab === 'overview' ? (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900">
                        {t("settings.payments.connected.title")}
                      </h4>
                      <p className="text-green-700 mt-1">
                        {t("settings.payments.connected.description")}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <SectionTitle text={t("settings.payments.payouts.title")} />
                    <div className="mt-2 rounded-md border p-4">
                      <p>{t("settings.payments.payouts.description")}</p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {t("settings.payments.payouts.schedule")}
                      </p>
                      {accountSummary && (
                        <div className="mt-4 grid gap-3 sm:grid-cols-3">
                          {renderAccountSummaryValue(
                            t("settings.payments.payouts.summary.available"),
                            accountSummary.availableBalanceCents
                          )}
                          {renderAccountSummaryValue(
                            t("settings.payments.payouts.summary.pending"),
                            accountSummary.pendingBalanceCents
                          )}
                          {renderAccountSummaryValue(
                            t("settings.payments.payouts.summary.in_transit"),
                            accountSummary.inTransitToBankCents
                          )}
                        </div>
                      )}
                      {isAccountSummaryLoading && !accountSummary && (
                        <p className="mt-4 text-sm text-muted-foreground">
                          {t("settings.payments.payouts.summary.loading")}
                        </p>
                      )}
                      {accountSummaryError && (
                        <p className="mt-4 text-sm text-muted-foreground">
                          {t("settings.payments.payouts.summary.error")}
                        </p>
                      )}
                    </div>
                  </div>
                </>
              ) : activeTab === 'invoices' ? (
                <InvoicesTab />
              ) : (
                <PaymentMethodsTab />
              )}
            </>
          ) : (
            <>
              {bannerConfig && (
                <Banner
                  title={bannerConfig.title}
                  description={bannerConfig.description}
                  primaryAction={{ 
                    label: isOnboarding 
                      ? t("settings.payments.banner.loading") 
                      : bannerConfig.actionLabel, 
                    onClick: bannerConfig.action 
                  }}
                  disabled={isOnboarding}
                />
              )}
              {bannerConfig && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-md border border-border bg-muted/30 px-4 py-3">
                  <p className="text-sm text-muted-foreground">
                    {t("settings.payments.help.note")}
                  </p>
                  <ButtonV2
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(CONFIG.CONTACT_INFO.WHATSAPP, '_blank', 'noopener,noreferrer')}
                    className="shrink-0"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    {t("settings.payments.help.cta")}
                  </ButtonV2>
                </div>
              )}
              <div>
                <SectionTitle text={t("settings.payments.payouts.title")} />
                <p>{t("settings.payments.payouts.no_data")}</p>
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">
            {isConnected 
              ? t("settings.payments.dashboard.connected_description")
              : t("settings.payments.dashboard.description")
            }
          </p>
          <div className="flex gap-2">
            {/* {isConnected && (
              <ButtonV2 
                onClick={navigateToStripeUpdatePage} 
                variant="outline" 
                disabled={isLoading}
              >
                {isLoading && loadingMessage === t("settings.payments.loading.generating_update_link")! ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Edit className="mr-2 h-4 w-4" />
                )}
                {t("settings.payments.account.edit")}
              </ButtonV2>
            )} */}
            <ButtonV2 
              onClick={navigateToStripeDashboard} 
              variant="outline" 
              disabled={!isConnected || isLoading}
            >
              {isLoading && loadingMessage === t("settings.payments.loading.generating_link")! ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ExternalLink className="mr-2 h-4 w-4" />
              )}
              {t("settings.payments.dashboard.cta")}
            </ButtonV2>
          </div>
        </CardFooter>
      </CardV2>

      <CardV2>
        <CardHeader>
          <CardTitle className="text-center">{t("global.faq.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <FAQ 
            items={[
              {
                id: 'payment-purpose',
                questionKey: 'settings.payments.faq.payment-purpose.question',
                answerKey: 'settings.payments.faq.payment-purpose.answer'
              },
              {
                id: 'customer-experience',
                questionKey: 'settings.payments.faq.customer-experience.question',
                answerKey: 'settings.payments.faq.customer-experience.answer'
              },
              {
                id: 'payment-proof',
                questionKey: 'settings.payments.faq.payment-proof.question',
                answerKey: 'settings.payments.faq.payment-proof.answer'
              },
              {
                id: 'verification-issues',
                questionKey: 'settings.payments.faq.verification-issues.question',
                answerKey: 'settings.payments.faq.verification-issues.answer'
              },
              {
                id: 'receipt-invoice-responsibility',
                questionKey: 'settings.payments.faq.receipt-invoice-responsibility.question',
                answerKey: 'settings.payments.faq.receipt-invoice-responsibility.answer'
              },
              {
                id: 'vat-responsibility',
                questionKey: 'settings.payments.faq.vat-responsibility.question',
                answerKey: 'settings.payments.faq.vat-responsibility.answer'
              },
              {
                id: 'invoice-generation',
                questionKey: 'settings.payments.faq.invoice-generation.question',
                answerKey: 'settings.payments.faq.invoice-generation.answer'
              },
              {
                id: 'accounting-documentation',
                questionKey: 'settings.payments.faq.accounting-documentation.question',
                answerKey: 'settings.payments.faq.accounting-documentation.answer'
              },
              {
                id: 'monthly-tasks',
                questionKey: 'settings.payments.faq.monthly-tasks.question',
                answerKey: 'settings.payments.faq.monthly-tasks.answer'
              },
              {
                id: 'payment-disruption',
                questionKey: 'settings.payments.faq.payment-disruption.question',
                answerKey: 'settings.payments.faq.payment-disruption.answer'
              },
              {
                id: 'onboarding-issues',
                questionKey: 'settings.payments.faq.onboarding-issues.question',
                answerKey: 'settings.payments.faq.onboarding-issues.answer'
              },
              {
                id: 'payment-flow',
                questionKey: 'settings.payments.faq.payment-flow.question',
                answerKey: 'settings.payments.faq.payment-flow.answer'
              }
            ]}
          />
        </CardContent>
      </CardV2>
    </div>
  )
}